import Image from 'next/image';
import cx from 'classnames';

import { Header } from '../../../components/Header';
import { PageMetadata } from '../../../components/PageMetadata';
import { Footer } from '../../../components/Footer/Footer';
import { Section, Row, Column } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { CallToActionSection } from '../../../components/CallToActionSection';

import { apolloClient, GetServerPageProps } from '../../../lib/apollo';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import Link from 'next/link';
import {
  getServerPageAgent,
  getServerPageAgentReviews,
  getServerPageAgentSlugs,
  useAgentReviewsLazyQuery,
} from './agent.generated';
import {
  ArrowLongRightIcon,
  ArrowLongLeftIcon,
  CalendarDaysIcon,
} from '@heroicons/react/20/solid';
import {
  MapPinIcon,
  PhoneIcon,
  ChatBubbleBottomCenterTextIcon,
  EnvelopeIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { Fragment, useMemo, useState } from 'react';
import {
  getCalendlyEventUris,
  lookupCalendlyAvailability,
} from '../../../lib/calendly';
import type {
  CalendlyAvailableTimes,
  CalendlyEventUris,
} from '../../../lib/calendly';
import { DateTime } from 'luxon';
import { useUpdateCalendlyAvailability } from '../../../hooks/useUpdateCalendlyAvailability';
import { Rating } from '../../../components/Rating';
import { aggregateReviews } from '../../../lib/reviewAggregation';
import { logEvent } from '../../../lib/analytics';
import { DateTimeDisplay } from '../../../components/DateTimeDisplay';
import { ServerTimezoneAlert } from '../../../components/ServerTimezoneAlert';
import { CalendlyModal } from '../../../components/CalendlyModal';
import { GoogleLogo } from '../../../components/GoogleLogo';
import { RichText } from '../../../lib/copyRendering';

const calendlyModalElement =
  typeof window !== 'undefined' && document.getElementById('__next');

export default function AgentPage({
  agent,
  reviews: initialReviews,
  aggregatedReviews,
  agentCalendlySchedulingUrls,
  calendlyEventUris,
  calendlyAvailableTimes: calendlyAvailableTimesFallback,
  slug,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const [calendlyModalUri, setCalendlyModalUri] = useState<string>();
  const [selectedBadgeIndex, setSelectedBadgeIndex] = useState(0);

  const firstName = useMemo(() => agent.name?.split(' ')[0], [agent]);
  const phoneNumber = parsePhoneNumberFromString(
    `+${agent.phoneNumber}`,
  )?.formatNational();
  const selectedBadge = useMemo(
    () => agent.badgesCollection?.items[selectedBadgeIndex],
    [agent, selectedBadgeIndex],
  );

  const { calendlyAvailableTimes } = useUpdateCalendlyAvailability({
    schedulingUrls: agentCalendlySchedulingUrls,
    calendlyEventUris,
    calendlyAvailableTimesFallback,
  });

  const calendlyUri = agent.calendlyUrl;
  const agentAvailableTimes = calendlyUri
    ? calendlyAvailableTimes?.[calendlyUri]
    : undefined;

  const firstAvailableDay = agentAvailableTimes?.days
    ? Object.values(agentAvailableTimes.days).find(
        (day) => day.totalTimeSlots > 0,
      )
    : undefined;

  const [
    loadReviews,
    {
      data: dataReviews,
      loading: loadingReviews,
      fetchMore: fetchMoreReviews,
      called: calledLoadReviews,
    },
  ] = useAgentReviewsLazyQuery({
    variables: { slug, limit: 10 },
    client: apolloClient, // Manually providing this here instead of using <ApolloProvider /> to keep Apollo out of the main bundle since very few pages load data client-side
  });

  const reviews = dataReviews?.reviewCollection ?? initialReviews;

  const showAwards =
    (agent.badgesCollection?.items.length ?? 0) > 0 && agent.active;

  const agentNameSection = (
    <Section background="BLUE">
      <Row>
        <Column>
          <Link href="/agents" className="flex items-center mb-5">
            <ArrowLongLeftIcon className="w-6 mr-2" />
            Back to all agents
          </Link>
          <Text.H1 className="font-bold lg:text-6xl mb-2">{agent.name}</Text.H1>
          <p className="text-blue-600">Licensed Blue Cross NC Agent</p>
        </Column>
      </Row>
    </Section>
  );

  const agentCardSection = (
    <Section background="WHITE">
      <Row>
        <Column className="text-center flex flex-col items-center">
          {agent.photo?.url && (
            <Image
              src={agent.photo.url}
              alt={agent.photo.description ?? ''}
              width={300}
              height={300}
              className="rounded-full"
              priority
            />
          )}
          <div className="mt-2">
            <Text.Small>
              <Link
                href={`/agents/${slug}#reviews`}
                className="text-blue-600 hover:text-blue-700 block text-left"
              >
                <Rating
                  stars={aggregatedReviews.average}
                  showNumber
                  className="w-5"
                />
                <div className="flex gap-2">
                  <GoogleLogo />
                  <p>
                    {aggregatedReviews.total}
                    {' Google '}
                    {aggregatedReviews.total === 1 ? 'Review' : 'Reviews'}
                  </p>
                </div>
              </Link>
            </Text.Small>
          </div>
          <p className="text-gray-500">
            <MapPinIcon className="w-4 inline mr-2" />
            {agent.city}, {agent.county} County
          </p>
        </Column>
        <Column>
          <Text.H2 className="md:text-2xl font-semibold">
            Contact {firstName}
          </Text.H2>
          {phoneNumber && (
            <a
              href={`tel:${phoneNumber}`}
              className="block text-blue-600 hover:text-blue-700"
              onClick={() =>
                logEvent({
                  category: 'Agent Page',
                  name: 'Phone link Click',
                })
              }
            >
              <PhoneIcon className="w-4 inline mr-2" />
              {phoneNumber}
            </a>
          )}
          {phoneNumber && (
            <a
              href={`sms:${phoneNumber}`}
              className="block text-blue-600 hover:text-blue-700"
              onClick={() =>
                logEvent({
                  category: 'Agent Page',
                  name: 'SMS link Click',
                })
              }
            >
              <ChatBubbleBottomCenterTextIcon className="w-4 inline mr-2" />
              {phoneNumber}
            </a>
          )}
          {agent.email && (
            <a
              href={`mailto:${agent.email}`}
              className="block text-blue-600 hover:text-blue-700"
              onClick={() =>
                logEvent({
                  category: 'Agent Page',
                  name: 'Email link click',
                })
              }
            >
              <EnvelopeIcon className="w-4 inline mr-2" />
              Email
            </a>
          )}
          {agent.businessCardUrl && (
            <a
              href={agent.businessCardUrl}
              className="block text-blue-600 hover:text-blue-700"
              onClick={() =>
                logEvent({
                  category: 'Agent Page',
                  name: 'eBusiness card link click',
                })
              }
            >
              <IdentificationIcon className="w-4 inline mr-2" />
              eBusiness Card
            </a>
          )}
          {agent.velocifyId && agent.active && (
            <Link
              href={`/agents/${slug}/quote`}
              className="block border bg-blue-500 hover:bg-blue-700 text-white rounded-xl px-3 py-2 text-center"
              onClick={() =>
                logEvent({
                  category: 'Agent Page',
                  name: 'Quote link click',
                })
              }
            >
              Get Quote from {firstName}
            </Link>
          )}
          {(agent.under65Redirect ||
            agent.over65Redirect ||
            agent.dentalRedirect) && (
            <div className="border border-gray-300 rounded-xl p-4 space-y-2 text-xs">
              <Text.H3 className="!text-sm md:!text-md">
                Customer Self-Quote & Enroll
              </Text.H3>
              {agent.under65Redirect && (
                <Link
                  href={agent.under65Redirect}
                  className="block text-blue-600 hover:text-blue-700"
                  onClick={() =>
                    logEvent({
                      category: 'Agent Page',
                      name: 'Self-enroll link click',
                      type: 'Under 65',
                    })
                  }
                >
                  Individual & Family/Affordable Care Act
                </Link>
              )}
              {agent.over65Redirect && (
                <Link
                  href={agent.over65Redirect}
                  className="block text-blue-600 hover:text-blue-700"
                  onClick={() =>
                    logEvent({
                      category: 'Agent Page',
                      name: 'Self-enroll link click',
                      type: 'Over 65',
                    })
                  }
                >
                  Medicare
                </Link>
              )}
              {agent.dentalRedirect && (
                <Link
                  href={agent.dentalRedirect}
                  className="block text-blue-600 hover:text-blue-700"
                  onClick={() =>
                    logEvent({
                      category: 'Agent Page',
                      name: 'Self-enroll link click',
                      type: 'Dental',
                    })
                  }
                >
                  Dental
                </Link>
              )}
            </div>
          )}
        </Column>
        {agent.calendlyUrl && (
          <Column className="border border-gray-300 rounded-2xl p-4 md:!p-6 text-center flex flex-col items-center">
            <Text.H3 className="md:text-2xl font-semibold">
              Schedule an appointment
            </Text.H3>
            <ServerTimezoneAlert />
            {firstAvailableDay && (
              <>
                <Text.Small>
                  <>
                    Next Available:{' '}
                    <DateTimeDisplay
                      dateString={firstAvailableDay.timeSlots[0].start_time}
                      format={DateTime.DATE_HUGE}
                      displayServerTimezone
                    />
                  </>
                </Text.Small>
                <div>
                  {firstAvailableDay.timeSlots.map((timeSlot) => (
                    <button
                      key={timeSlot.start_time}
                      onClick={() => {
                        setCalendlyModalUri(timeSlot.scheduling_url);
                        logEvent({
                          category: 'Agent Page',
                          name: 'Time slot button clicked',
                          calendlyUri: timeSlot.scheduling_url,
                        });
                      }}
                      className="px-4 py-2 mx-2 !mb-2 border border-blue-500 hover:bg-blue-100 rounded-xl"
                    >
                      <Text.Small className="!text-blue-500">
                        <DateTimeDisplay
                          dateString={timeSlot.start_time}
                          format={DateTime.TIME_SIMPLE}
                          displayServerTimezone
                        />
                      </Text.Small>
                    </button>
                  ))}
                </div>
              </>
            )}
            <button
              onClick={() => {
                setCalendlyModalUri(
                  agent.calendlyUrlFullCalendarOverride ??
                    agent.calendlyUrl ??
                    undefined,
                );
                logEvent({
                  category: 'Agent Page',
                  name: 'See full calendar button clicked',
                  calendlyUri: agent.calendlyUrl,
                });
              }}
              className="text-blue-500 rounded-xl hover:opacity-75 text-center flex items-center"
            >
              <CalendarDaysIcon className="w-5 inline mr-2" />
              View full calendar
            </button>
            {phoneNumber && (
              <p className="text-xs">
                Questions? Call or text {firstName} at{' '}
                <a
                  href={`tel:${phoneNumber}`}
                  className="text-blue-600 hover:text-blue-700 whitespace-nowrap"
                  onClick={() =>
                    logEvent({
                      category: 'Agent Page',
                      name: 'Scheduling phone link click',
                    })
                  }
                >
                  {phoneNumber}
                </a>
              </p>
            )}
            <CalendlyModal
              calendlyModalUri={calendlyModalUri}
              onClose={() => setCalendlyModalUri(undefined)}
            />
          </Column>
        )}
      </Row>
    </Section>
  );
  const agentNavSection = (
    <Section background="BLUE">
      <Row>
        <Column>
          <span className="isolate inline-flex rounded-xl shadow-sm w-full">
            {[
              { href: '#about', label: 'About' },
              { href: '#reviews', label: 'Reviews' },
              ...(showAwards ? [{ href: '#awards', label: 'Awards' }] : []),
            ].map(({ href, label }, index, items) => (
              <Link
                key={href}
                href={href}
                scroll={false}
                className={cx(
                  'flex-auto relative inline-flex justify-center items-center border border-gray-300 bg-white px-4 py-6 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
                  { '-ml-px': index > 0 },
                  { 'rounded-l-xl': index === 0 },
                  { 'rounded-r-xl': index === items.length - 1 },
                )}
              >
                {label}
              </Link>
            ))}
          </span>
        </Column>
      </Row>
    </Section>
  );

  const agentAboutSection = (
    <Section background="WHITE">
      <Row>
        <Column>
          <Text.H2 id="about" className="scroll-mt-24">
            About {firstName}
          </Text.H2>
          <Text.H3 className="mt-6 md:text-2xl font-medium">Biography</Text.H3>
          <Text.Body className="max-w-prose !text-gray-500">
            <RichText content={agent.biography} />
          </Text.Body>
        </Column>
      </Row>
      <Row>
        <Column>
          <Text.H3 className="mt-6 md:text-2xl font-medium">Products</Text.H3>
          <ul className="text-gray-500 space-y-1">
            {agent.products?.map((product) => <li key={product}>{product}</li>)}
          </ul>
        </Column>
        <Column>
          <Text.H3 className="mt-6 md:text-2xl font-medium">
            Insurance Carriers
          </Text.H3>
          <ul className="text-gray-500 space-y-1">
            {agent.insuranceCarrier?.map((carrier) => (
              <li key={carrier}>{carrier}</li>
            ))}
          </ul>
        </Column>
        <Column>
          <Text.H3 className="mt-6 md:text-2xl font-medium">Languages</Text.H3>
          <ul className="text-gray-500 space-y-1">
            {agent.languages?.map((language) => (
              <li key={language}>{language}</li>
            ))}
          </ul>
          <Text.H3 className="mt-6 md:text-2xl font-medium">
            Licensed in
          </Text.H3>
          <Text.Body className="!text-gray-500 ">
            {agent.licensedYear}
          </Text.Body>
        </Column>
      </Row>
    </Section>
  );

  const agentReviewsSection = (
    <Section background="BLUE">
      <Row>
        <Column>
          <Text.H2 id="reviews" className="scroll-mt-24">
            Customer Reviews
          </Text.H2>
        </Column>
      </Row>
      <Row>
        <Column>
          <div className="flex flex-col">
            <Rating
              stars={aggregatedReviews.average}
              showNumber
              className="w-5"
            />
            <div className="flex">
              <GoogleLogo />
              <span className="ml-2 text-sm text-gray-500">
                {aggregatedReviews.average.toFixed(1)} based on{' '}
                {aggregatedReviews.total}
                {' Google '}
                {aggregatedReviews?.total === 1 ? 'review' : 'reviews'}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-[min-content,min-content,auto,min-content] items-center gap-2">
            {Object.entries(aggregatedReviews.bucketedCounts)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([stars, count]) => {
                const percent = aggregatedReviews.total
                  ? Math.floor((count / aggregatedReviews.total) * 100)
                  : 0;
                return (
                  <Fragment key={stars}>
                    {stars}
                    <Rating stars={1} maxStars={1} />
                    <div className="w-full bg-gray-100 border border-gray-400/20 h-3 rounded-xl">
                      <div
                        className="h-full bg-yellow-400 rounded-xl z-10"
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                    <span className="justify-self-end">{percent}%</span>
                  </Fragment>
                );
              })}
          </div>
          <p>
            Agent&apos;s reviews are unedited and posted from{' '}
            <a
              href="https://g.page/HealthPlansofNC/review?gm"
              className="text-blue-600 hover:text-blue-700"
            >
              Google
            </a>
            .
          </p>
          <p>
            <Link
              href="/article/why-we-use-google-reviews"
              className="text-blue-600 hover:text-blue-700"
            >
              Why we use Google Reviews
            </Link>
          </p>
        </Column>

        <Column>
          {reviews?.items.map((review) =>
            review?.starRating ? (
              <div
                key={review.sys.id}
                className="border-b border-gray-200 py-4 !my-0"
              >
                <p className="font-bold">{review.name}</p>
                <Rating stars={review.starRating} showNumber />
                <div className="text-gray-600">
                  <DateTimeDisplay
                    dateString={review.date}
                    format={DateTime.DATE_FULL}
                    displayServerTimezone
                  />
                </div>
                <p className="text-gray-600 italic mt-2">{review.content}</p>
              </div>
            ) : null,
          )}
          {aggregatedReviews.total > (reviews?.items.length ?? 3) && (
            <button
              className="flex items-center mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
              onClick={() =>
                calledLoadReviews
                  ? fetchMoreReviews({
                      variables: {
                        skip: reviews?.items.length,
                      },
                    })
                  : loadReviews()
              }
              disabled={loadingReviews}
            >
              {loadingReviews ? (
                <>Loading...</>
              ) : (
                <>
                  See more reviews <ArrowLongRightIcon className="w-6 ml-2" />
                </>
              )}
            </button>
          )}
        </Column>
      </Row>
    </Section>
  );

  const agentAwardsSection = showAwards && (
    <Section background="WHITE">
      <Row>
        <Column>
          <Text.H2 id="awards" className="scroll-mt-24">
            {firstName}&apos;s Awards
          </Text.H2>
          <Text.Body className="max-w-prose !text-gray-500">
            The{' '}
            <Link
              href="/article/what-is-the-health-insurance-marketplace-circle-of-champions"
              className="text-blue-600 hover:underline"
            >
              Marketplace Circle of Champions
            </Link>{' '}
            is an annual recognition program that rewards the hard work,
            expertise, and service of registered agents and brokers. The most
            recent awards earned are shown below.
          </Text.Body>
        </Column>
      </Row>
      <Row>
        {selectedBadge?.image?.url && (
          <Column className="flex flex-col items-center text-center text-gray-500 !space-y-2">
            <Image
              src={selectedBadge.image.url}
              width={200}
              height={200}
              alt={selectedBadge.name ?? ''}
            />
            <Text.Body className="font-bold">{selectedBadge.year}</Text.Body>
            <Text.Body>{selectedBadge.name?.replace(/^\d{4}\s/, '')}</Text.Body>
            <Text.Body className="max-w-xs mx-auto leading-tight">
              {selectedBadge.description}
            </Text.Body>
          </Column>
        )}
        <Column>
          <Text.H3 className="mt-6 md:text-2xl font-medium">
            History of Awards
          </Text.H3>
          <ul className="text-gray-500 space-y-1">
            {agent.badgesCollection?.items?.map((badge, index) => (
              <li
                key={badge?.year}
                className={cx(
                  'flex items-center hover:opacity-80 cursor-pointer',
                  { 'text-gray-800': index === selectedBadgeIndex },
                )}
                onClick={() => setSelectedBadgeIndex(index)}
              >
                {badge?.image?.url && (
                  <Image
                    src={badge.image.url}
                    width={20}
                    height={20}
                    alt={badge?.name ?? ''}
                  />
                )}
                <span className="ml-2">{badge?.name}</span>
              </li>
            ))}
          </ul>
        </Column>
        <Column className="hidden lg:block" />
      </Row>
    </Section>
  );

  return (
    <>
      <PageMetadata
        pageTitle={agent.name ?? undefined}
        description={`Meet ${agent.name}, a North Carolina agent specializing in Health Insurance, Medicare, Short Term, Obamacare, Dental, Vision, Individual &amp; Family.`}
        openGraphImage={agent.photo?.url ?? undefined}
      />
      <Header />

      {agentNameSection}
      {agentCardSection}
      {agentNavSection}
      {agentAboutSection}
      {agentReviewsSection}
      {agentAwardsSection}

      <CallToActionSection quoteAction />
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  agent: NonNullable<
    NonNullable<
      GetServerPageProps<typeof getServerPageAgent>['data']['agentCollection']
    >['items'][0]
  >;
  reviews: GetServerPageProps<
    typeof getServerPageAgentReviews
  >['data']['reviewCollection'];
  aggregatedReviews: ReturnType<typeof aggregateReviews>;
  calendlyAvailableTimes: CalendlyAvailableTimes;
  calendlyEventUris: CalendlyEventUris;
  agentCalendlySchedulingUrls: string[];
  slug: string;
}> = async ({ params }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug[0] : params?.slug;

  if (!slug) {
    throw new Error('slug was not defined');
  }

  const agentQuery = (
    await getServerPageAgent({
      variables: {
        slug,
      },
    })
  ).props;

  const agent = agentQuery.data.agentCollection?.items[0];

  const reviews = (
    await getServerPageAgentReviews({
      variables: {
        slug,
      },
    })
  ).props.data.reviewCollection;
  const allReviews = agentQuery.data.reviewAggregationCollection;

  if (!agent) {
    throw new Error('Agent was not defined');
  }

  const calendlyEventUris = agent.calendlyUrl
    ? {
        [agent.calendlyUrl]:
          (await getCalendlyEventUris())[agent.calendlyUrl] ?? null,
      }
    : {};
  const agentCalendlySchedulingUrls = agent.calendlyUrl
    ? [agent.calendlyUrl]
    : [];
  const calendlyAvailableTimes = calendlyEventUris
    ? await lookupCalendlyAvailability(
        agentCalendlySchedulingUrls,
        calendlyEventUris,
      )
    : {};

  return {
    props: {
      agent,
      reviews,
      aggregatedReviews: aggregateReviews({ reviews: allReviews }),
      calendlyEventUris,
      agentCalendlySchedulingUrls,
      calendlyAvailableTimes,
      slug,
    },
  };
};

export async function getStaticPaths() {
  const { props } = await getServerPageAgentSlugs({});
  return {
    paths: props.data.agentCollection?.items.map((agent) => ({
      params: {
        slug: agent?.slug,
      },
    })),
    fallback: false,
  };
}
